import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { TICKET_STATUS_OPTIONS, TICKET_URGENCY_OPTIONS, SUPPORT_TYPE_OPTIONS } from '../../utils/ticketConstants';

/**
 * Filter drawer for ticket filtering
 */
const FilterDrawer = ({
  open,
  onClose,
  filter,
  onFilterChange,
  onResetFilter
}) => {
  const { t } = useThemeSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleApply = () => {
    onClose();
  };

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 320,
          maxHeight: isMobile ? '80vh' : '100%',
          borderTopLeftRadius: isMobile ? 16 : 0,
          borderTopRightRadius: isMobile ? 16 : 0
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
        <Typography variant="h6">
          {t ? t('Tickets.filters') : 'Filters'}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Filter Controls */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {/* Status Filter */}
        <FormControl fullWidth size="small">
          <InputLabel>{t ? t('Tickets.status') : 'Status'}</InputLabel>
          <Select
            multiple
            value={filter.status}
            onChange={(e) => onFilterChange('status', e.target.value)}
            label={t ? t('Tickets.status') : 'Status'}
            renderValue={(selected) => selected.join(', ')}
          >
            {TICKET_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Urgency Filter */}
        <FormControl fullWidth size="small">
          <InputLabel>{t ? t('Tickets.urgency') : 'Urgency'}</InputLabel>
          <Select
            value={filter.urgency}
            onChange={(e) => onFilterChange('urgency', e.target.value)}
            label={t ? t('Tickets.urgency') : 'Urgency'}
          >
            <MenuItem value="">
              <em>{t ? t('Common.all') : 'All'}</em>
            </MenuItem>
            {TICKET_URGENCY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Support Type Filter */}
        <FormControl fullWidth size="small">
          <InputLabel>{t ? t('Tickets.support_type') : 'Support Type'}</InputLabel>
          <Select
            value={filter.support_type}
            onChange={(e) => onFilterChange('support_type', e.target.value)}
            label={t ? t('Tickets.support_type') : 'Support Type'}
          >
            <MenuItem value="">
              <em>{t ? t('Common.all') : 'All'}</em>
            </MenuItem>
            {SUPPORT_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Next Step Filter */}
        <TextField
          fullWidth
          size="small"
          label={t ? t('Tickets.next_step') : 'Next Step'}
          value={filter.next_step}
          onChange={(e) => onFilterChange('next_step', e.target.value)}
          placeholder={t ? t('Tickets.next_step_placeholder') : 'Filter by next step...'}
          helperText={t ? t('Tickets.wildcard_hint') : 'Use * for wildcard'}
        />
      </Box>

      <Divider />

      {/* Actions */}
      <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onResetFilter}
        >
          {t ? t('Tickets.reset_filters') : 'Reset Filters'}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleApply}
        >
          {t ? t('Common.apply') : 'Apply'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
